import { LvfHandlingType } from '../../../enum/supplychain/lvf-handling-type';
import { LvfVehicleStructureType } from '../../../enum/supplychain/lvf-vehicle-structure-type';
import { LvfAppModule } from '../../../enum/application/lvf-app-module';
import { LvfEquipmentType, carriesVehicleExtension } from '../../../enum/supplychain/lvf-equipment-type';
import { Guid } from '../../../types/guid.type';
import { EntityBase, IEntityBaseInfo } from '../../entity/entitybase';
import { IPersonInfo, PersonInfo } from '../../core/person-info';
import { IMultiStatusInfo, MultiStatus } from '../../multi-status/multi-status';
import { IMultiStatusConfigInfo, MultiStatusConfig } from '../../multi-status/multi-status-config';
import { EquipmentVehicleTypeExtension, IEquipmentVehicleTypeExtensionInfo } from './equipment-vehicle-type-extension';

export interface IEquipmentTypeInfo extends IEntityBaseInfo {
  Label?: string;
  Icon?: string;
  Description?: string;
  AuthorUid?: Guid;
  Author?: IPersonInfo;
  TaxAmount?: number;
  RebillingSemiAmount?: number;
  CanbeReserved?: boolean;
  LvfEquipmentType?: LvfEquipmentType;
  LvfHandlingType?: LvfHandlingType;
  StatusLvfAppModule?: LvfAppModule;
  VehicleTypeExtension?: IEquipmentVehicleTypeExtensionInfo;
  RequiresMonthlyMileage?: boolean;
  LinkedDocumentTypeUids?: Guid[];
}

export class EquipmentType extends EntityBase {
  Label?: string;
  Icon?: string;
  Description?: string;
  AuthorUid?: Guid;
  Author?: PersonInfo;
  TaxAmount?: number;
  RebillingSemiAmount?: number;
  CanbeReserved?: boolean;
  LvfEquipmentType?: LvfEquipmentType;
  LvfHandlingType?: LvfHandlingType;
  StatusLvfAppModule?: LvfAppModule;
  VehicleTypeExtension?: EquipmentVehicleTypeExtension;
  RequiresMonthlyMileage?: boolean;
  LinkedDocumentTypeUids?: Guid[];

  // Client side properties
  CanbeReservedConfig?: MultiStatusConfig;
  LvfEquipmentTypeLabel?: string;
  LvfHandlingTypeLabel?: string;
  LvfVehicleStructureTypeLabel?: string;

  constructor(info: IEquipmentTypeInfo) {
    super(info);
    this.Label = info.Label;
    this.Icon = info.Icon;
    this.Description = info.Description;
    this.AuthorUid = info.AuthorUid;
    this.Author = PersonInfo.createInstance(PersonInfo, info.Author);
    this.TaxAmount = info.TaxAmount;
    this.RebillingSemiAmount = info.RebillingSemiAmount;
    this.CanbeReserved = info.CanbeReserved ?? false;
    this.LvfEquipmentType = info.LvfEquipmentType;
    this.LvfHandlingType = info.LvfHandlingType;
    this.StatusLvfAppModule = info.StatusLvfAppModule;
    this.RequiresMonthlyMileage = info.RequiresMonthlyMileage ?? false;
    this.LinkedDocumentTypeUids = info.LinkedDocumentTypeUids ?? [];
    this.VehicleTypeExtension = EquipmentType.createVehicleTypeExtension(info);

    this.CanbeReservedConfig = this.buildCanbeReservedConfig();
    this.changeCanbeReservedStyle();

    // Client side calculated properties
    this.LvfEquipmentTypeLabel = this.LvfEquipmentType ? `app.lvfequipmenttype.${LvfEquipmentType[this.LvfEquipmentType].toLowerCase()}` : '';
    this.LvfHandlingTypeLabel = this.LvfHandlingType ? `app.lvfhandlingtype.${LvfHandlingType[this.LvfHandlingType].toLowerCase()}` : '';
    const structureType = this.VehicleTypeExtension?.LvfVehicleStructureType;
    this.LvfVehicleStructureTypeLabel = structureType ? `app.lvfvehiclestructuretype.${LvfVehicleStructureType[structureType].toLowerCase()}` : '';
  }

  static carriesVehicleExtension(family?: LvfEquipmentType): boolean {
    return carriesVehicleExtension(family);
  }

  private static createVehicleTypeExtension(info: IEquipmentTypeInfo): EquipmentVehicleTypeExtension | undefined {
    if (!carriesVehicleExtension(info.LvfEquipmentType)) {
      return undefined;
    }

    return info.VehicleTypeExtension
      ? new EquipmentVehicleTypeExtension(info.VehicleTypeExtension)
      : new EquipmentVehicleTypeExtension({});
  }

  override title(): string {
    return `Type d'équipement : ${this.Label}`;
  }

  override description(): string {
    return `Description : ${this.Description || 'Aucune description'}`;
  }

  buildCanbeReservedConfig(): MultiStatusConfig {
    return new MultiStatusConfig(<IMultiStatusConfigInfo>{
      Statuses: [
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.vehicle.type.reserved',
          Style: 'color: var(--dropdown-bg); background-color: var(--purple-success)',
          Handler: () => {
            this.CanbeReserved = true;
            this.changeCanbeReservedStyle();
          },
        }),
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.vehicle.type.notReserved',
          Style: 'color: var(--module-title); background-color: var(--dropdown-bg)',
          Handler: () => {
            this.CanbeReserved = false;
            this.changeCanbeReservedStyle();
          },
        }),
      ],
    });
  }

  changeCanbeReservedStyle(): void {
    if (!this.CanbeReservedConfig) return;

    if (this.CanbeReserved) {
      this.CanbeReservedConfig.Statuses[0].Style = 'color: var(--dropdown-bg); background-color: var(--purple-success)';
      this.CanbeReservedConfig.Statuses[1].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
    } else {
      this.CanbeReservedConfig.Statuses[0].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
      this.CanbeReservedConfig.Statuses[1].Style = 'color: var(--dropdown-bg); background-color: #C6C8D3';
    }
  }
}
